<template>
  <v-content class="gb signup-waitlist-page">
    <v-container fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" md="12" class="top-container">
          <div class="waitlist-card">
            <!-- Header -->
            <div class="waitlist-card__header">
              <h2 class="waitlist-card__title">
                Sign-Up Waitlist
                <span class="waitlist-card__title-chip">{{ requests.length }} total</span>
              </h2>
              <v-spacer></v-spacer>
              <div class="waitlist-card__search">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="waitlist-card__search-icon">
                  <path d="M10 18a8 8 0 1 1 5.29-14.03A8 8 0 0 1 10 18zm11 3l-6.34-6.34" stroke="#94a3b8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <input
                  v-model="search"
                  type="text"
                  placeholder="Search by name, phone, email, or company…"
                  class="waitlist-card__search-input"
                />
              </div>
            </div>

            <!-- Stat chip bar -->
            <div class="waitlist-stats-bar">
              <button
                type="button"
                class="waitlist-stats-bar__item"
                :class="{ 'waitlist-stats-bar__item--active': activeFilter === 'all' }"
                @click="activeFilter = 'all'"
              >
                <span class="waitlist-stats-bar__label">All</span>
                <span class="waitlist-stats-bar__count">{{ nonReRoutedCount }}</span>
              </button>
              <div class="waitlist-stats-bar__divider"></div>
              <button
                type="button"
                class="waitlist-stats-bar__item"
                :class="{ 'waitlist-stats-bar__item--active': activeFilter === 'need_agent' }"
                @click="activeFilter = 'need_agent'"
              >
                <span class="waitlist-stats-bar__dot waitlist-stats-bar__dot--orange"></span>
                <span class="waitlist-stats-bar__label">Need Agent</span>
                <span class="waitlist-stats-bar__count">{{ needAgentCount }}</span>
              </button>
              <div class="waitlist-stats-bar__divider"></div>
              <button
                type="button"
                class="waitlist-stats-bar__item"
                :class="{ 'waitlist-stats-bar__item--active': activeFilter === 'matched' }"
                @click="activeFilter = 'matched'"
              >
                <span class="waitlist-stats-bar__dot waitlist-stats-bar__dot--green"></span>
                <span class="waitlist-stats-bar__label">Matched</span>
                <span class="waitlist-stats-bar__count">{{ matchedCount }}</span>
              </button>
              <div class="waitlist-stats-bar__divider"></div>
              <button
                type="button"
                class="waitlist-stats-bar__item"
                :class="{ 'waitlist-stats-bar__item--active': activeFilter === 'pending' }"
                @click="activeFilter = 'pending'"
              >
                <span class="waitlist-stats-bar__dot waitlist-stats-bar__dot--yellow"></span>
                <span class="waitlist-stats-bar__label">Pending</span>
                <span class="waitlist-stats-bar__count">{{ pendingCount }}</span>
              </button>
              <div class="waitlist-stats-bar__divider"></div>
              <button
                type="button"
                class="waitlist-stats-bar__item"
                :class="{ 'waitlist-stats-bar__item--active': activeFilter === 're_routed' }"
                @click="activeFilter = 're_routed'"
              >
                <span class="waitlist-stats-bar__dot waitlist-stats-bar__dot--blue"></span>
                <span class="waitlist-stats-bar__label">Re-Routed</span>
                <span class="waitlist-stats-bar__count">{{ reRoutedCount }}</span>
              </button>
            </div>

            <!-- Active column-filter chips -->
            <div v-if="activeColumnFilters.length" class="waitlist-active-filters">
              <span
                v-for="chip in activeColumnFilters"
                :key="chip.col + '::' + chip.value"
                class="waitlist-filter-pill"
              >
                <span class="waitlist-filter-pill__label">{{ chip.label }}:</span>
                {{ chip.display }}
                <span @click="removeColumnFilter(chip.col, chip.value)" class="waitlist-filter-pill__close">&times;</span>
              </span>
              <button type="button" class="waitlist-filter-clear" @click="clearAllColumnFilters">Clear all</button>
            </div>

            <!-- Loading / empty states -->
            <div v-if="loading" class="waitlist-empty">
              <v-progress-circular indeterminate color="#2F6FED" size="28" width="3"></v-progress-circular>
              <p>Loading waitlist…</p>
            </div>

            <div v-else-if="!requests.length" class="waitlist-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style="margin-bottom: 12px;">
                <path d="M4 6h16M4 12h10M4 18h7" stroke="#cbd5e1" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <p>No sign-up requests yet.</p>
              <p class="waitlist-empty__sub">When agents, vendors, or clients sign up, they'll appear here.</p>
            </div>

            <div v-else-if="!filteredRequests.length" class="waitlist-empty">
              <p>No results match your filters.</p>
              <button type="button" class="waitlist-filter-clear" @click="resetFilters">Reset filters</button>
            </div>

            <!-- Table -->
            <div v-else class="waitlist-table-wrap">
              <table class="waitlist-table">
                <thead>
                  <tr>
                    <th class="waitlist-th--name">Name</th>
                    <th>
                      <button type="button" class="waitlist-th-button" :class="{ 'waitlist-th-button--active': openMenu === 'role' }" @click.stop="toggleHeaderMenu('role', $event)">
                        Role
                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" class="waitlist-th-button__chevron"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      </button>
                    </th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Company</th>
                    <th>Service</th>
                    <th>Location</th>
                    <th>
                      <button type="button" class="waitlist-th-button" :class="{ 'waitlist-th-button--active': openMenu === 'matched_company_name' }" @click.stop="toggleHeaderMenu('matched_company_name', $event)">
                        Matched Team
                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" class="waitlist-th-button__chevron"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      </button>
                    </th>
                    <th>Referred By</th>
                    <th>Referred From</th>
                    <th>
                      <button type="button" class="waitlist-th-button" :class="{ 'waitlist-th-button--active': openMenu === 'utm_source' }" @click.stop="toggleHeaderMenu('utm_source', $event)">
                        Source
                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" class="waitlist-th-button__chevron"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      </button>
                    </th>
                    <th>
                      <button type="button" class="waitlist-th-button" :class="{ 'waitlist-th-button--active': openMenu === 'status' }" @click.stop="toggleHeaderMenu('status', $event)">
                        Status
                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" class="waitlist-th-button__chevron"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      </button>
                    </th>
                    <th class="waitlist-th--date">Date</th>
                    <th class="waitlist-th--actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in paginatedRequests" :key="item.id" class="waitlist-row">
                    <td class="waitlist-cell--name">
                      <div class="waitlist-name">{{ item.first_name }} {{ item.last_name }}</div>
                    </td>
                    <td>
                      <span
                        class="waitlist-pill"
                        :style="rolePillStyle(item.role)"
                      >{{ item.role }}</span>
                    </td>
                    <td class="waitlist-cell--muted">{{ item.phone_number || '—' }}</td>
                    <td class="waitlist-cell--muted">{{ item.email || '—' }}</td>
                    <td class="waitlist-cell--muted">{{ item.company_name || '—' }}</td>
                    <td class="waitlist-cell--muted" style="text-transform: capitalize;">{{ item.service || '—' }}</td>
                    <td class="waitlist-cell--muted">
                      <span v-if="item.location">
                        {{ item.location.city || '' }}<span v-if="item.location.city && item.location.state">, </span>{{ item.location.state || '' }}
                      </span>
                      <span v-else class="waitlist-cell--dash">—</span>
                    </td>
                    <td>
                      <span v-if="item.matched_company_name" class="waitlist-cell--matched">{{ item.matched_company_name }}</span>
                      <span v-else class="waitlist-pill" style="background:#FFF3E0; color:#E65100;">Need Agent</span>
                    </td>
                    <td class="waitlist-cell--muted">
                      <span v-if="item.original_invited_by_agent_name">{{ item.original_invited_by_agent_name }}</span>
                      <span v-else class="waitlist-cell--dash">—</span>
                    </td>
                    <td class="waitlist-cell--muted">
                      <span v-if="item.original_referral_company_name">{{ item.original_referral_company_name }}</span>
                      <span v-else class="waitlist-cell--dash">—</span>
                    </td>
                    <td class="waitlist-cell--muted">
                      <span v-if="item.utm_source" style="text-transform: capitalize;">
                        {{ displaySource(item.utm_source) }}<span v-if="item.utm_medium" style="color:#94a3b8;"> / {{ item.utm_medium }}</span>
                      </span>
                      <span v-else class="waitlist-cell--dash">Direct</span>
                    </td>
                    <td>
                      <span
                        class="waitlist-pill"
                        :style="{ background: statusColor(item.status), color: statusTextColor(item.status) }"
                      >{{ displayStatus(item.status) }}</span>
                    </td>
                    <td class="waitlist-cell--date">{{ formatDate(item.created_at) }}</td>
                    <td class="waitlist-cell--actions">
                      <template v-if="item.status === 'pending' && item.matched_tenant_id">
                        <button
                          type="button"
                          class="waitlist-action-btn waitlist-action-btn--approve"
                          :disabled="reviewingId === item.id"
                          @click="reviewRequest(item, 'approve')"
                        >Approve</button>
                        <button
                          type="button"
                          class="waitlist-action-btn waitlist-action-btn--decline"
                          :disabled="reviewingId === item.id"
                          @click="reviewRequest(item, 'decline')"
                        >Decline</button>
                      </template>
                      <span v-else class="waitlist-cell--dash">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Pagination -->
              <div class="waitlist-pagination">
                <div class="waitlist-pagination__size">
                  Rows per page
                  <select v-model.number="pageSize" class="waitlist-pagination__select">
                    <option :value="25">25</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                  </select>
                </div>
                <div class="waitlist-pagination__info">
                  {{ paginationLabel }}
                </div>
                <div class="waitlist-pagination__nav">
                  <button type="button" class="waitlist-pagination__btn" :disabled="page === 1" @click="page = Math.max(1, page - 1)" aria-label="Previous page">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </button>
                  <button type="button" class="waitlist-pagination__btn" :disabled="page >= totalPages" @click="page = Math.min(totalPages, page + 1)" aria-label="Next page">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Single floating dropdown, teleported to body via `position: fixed`
         so the parent table's overflow cannot clip it. Content switches
         based on `openMenu`. -->
    <div
      v-if="openMenu"
      v-click-outside="closeHeaderMenu"
      class="waitlist-th-menu"
      :style="{ top: menuPosition.top + 'px', left: menuPosition.left + 'px', minWidth: menuPosition.width + 'px' }"
      @click.stop
    >
      <div class="waitlist-th-menu__header">{{ columnLabel(openMenu) }}</div>
      <label v-for="opt in uniqueValues(openMenu)" :key="openMenu + '::' + opt" class="waitlist-th-menu__item">
        <input type="checkbox" :checked="columnFilters[openMenu].includes(opt)" @change="toggleColumnFilter(openMenu, opt)" />
        <span>{{ formatMenuLabel(openMenu, opt) }}</span>
      </label>
      <button v-if="columnFilters[openMenu].length" type="button" class="waitlist-th-menu__clear" @click="columnFilters[openMenu] = []">Clear</button>
    </div>
  </v-content>
</template>

<script>
import axios from "axios";
import { auth } from "@/config/firebase";
import config from "@/config/constants.js";
import { mapState } from "vuex";

// Send BOTH the Firebase ID token (preferred — backend Tier 1) and the legacy
// Kiey JWT (Tier 2 fallback). The backend accepts either; sending both lets
// the request succeed even if one path is unavailable for a given session.
async function buildAuthHeaders() {
  const headers = {};
  try {
    const fbUser = auth.currentUser;
    if (fbUser) headers["X-Firebase-Token"] = await fbUser.getIdToken();
  } catch (e) {
    console.warn("[Signup Waitlist] failed to get Firebase ID token:", e && e.message);
  }
  const legacyToken = localStorage.getItem("auth_token")
    || (axios.defaults.headers && axios.defaults.headers.common && axios.defaults.headers.common["Authorization"])
    || "";
  if (legacyToken) headers["Authorization"] = legacyToken;
  return headers;
}

// Click-outside directive for the dropdown menu. Bubble phase so that
// @click.stop on the trigger button can prevent this handler from
// re-closing a just-opened menu.
const clickOutside = {
  bind(el, binding) {
    el._clickOutside = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.addEventListener('click', el._clickOutside, false);
  },
  unbind(el) {
    document.removeEventListener('click', el._clickOutside, false);
    delete el._clickOutside;
  },
};

const COLUMN_LABELS = {
  role: 'Role',
  matched_company_name: 'Matched Team',
  utm_source: 'Source',
  status: 'Status',
};

export default {
  name: "SignupWaitlist",
  directives: { 'click-outside': clickOutside },
  data() {
    return {
      loading: false,
      requests: [],
      search: "",
      activeFilter: "all",
      reviewingId: null,
      openMenu: null,
      menuPosition: { top: 0, left: 0, width: 200 },
      columnFilters: {
        role: [],
        matched_company_name: [],
        utm_source: [],
        status: [],
      },
      page: 1,
      pageSize: 25,
    };
  },
  computed: {
    ...mapState({
      user: state => state.Authenticate.user,
    }),
    filteredRequests() {
      let items = this.requests;

      // The Re-Routed tab is the ONLY place re-routed rows show up. Every
      // other tab (All / Need Agent / Matched / Pending) excludes them so
      // they don't double-count or pollute the operator's main views.
      if (this.activeFilter === 're_routed') {
        items = items.filter(r => this.isReRouted(r));
      } else {
        items = items.filter(r => !this.isReRouted(r));
        if (this.activeFilter === 'need_agent') {
          items = items.filter(r => !r.matched_tenant_id);
        } else if (this.activeFilter === 'matched') {
          items = items.filter(r => !!r.matched_tenant_id);
        } else if (this.activeFilter === 'pending') {
          items = items.filter(r => r.status === 'pending');
        }
      }

      for (const col of Object.keys(this.columnFilters)) {
        const vals = this.columnFilters[col];
        if (!vals.length) continue;
        items = items.filter(r => vals.includes(this.columnValue(r, col)));
      }

      if (this.search && this.search.trim()) {
        const q = this.search.trim().toLowerCase();
        items = items.filter(r => {
          const blob = [
            r.first_name, r.last_name, r.phone_number, r.email,
            r.company_name, r.service, r.matched_company_name,
            r.utm_source, r.status, r.role,
            r.original_invited_by_agent_name, r.original_referral_company_name,
          ].filter(Boolean).join(' ').toLowerCase();
          return blob.includes(q);
        });
      }

      return items;
    },
    paginatedRequests() {
      const start = (this.page - 1) * this.pageSize;
      return this.filteredRequests.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredRequests.length / this.pageSize));
    },
    paginationLabel() {
      if (!this.filteredRequests.length) return '0 of 0';
      const start = (this.page - 1) * this.pageSize + 1;
      const end = Math.min(start + this.pageSize - 1, this.filteredRequests.length);
      return `${start}–${end} of ${this.filteredRequests.length}`;
    },
    nonReRoutedCount() {
      // Used by the "All" pill — agrees with what the All tab displays
      // (re-routed rows are excluded from All; they live exclusively in their own tab).
      return this.requests.filter(r => !this.isReRouted(r)).length;
    },
    needAgentCount() {
      return this.requests.filter(r => !this.isReRouted(r) && !r.matched_tenant_id).length;
    },
    matchedCount() {
      return this.requests.filter(r => !this.isReRouted(r) && !!r.matched_tenant_id).length;
    },
    pendingCount() {
      return this.requests.filter(r => !this.isReRouted(r) && r.status === 'pending').length;
    },
    reRoutedCount() {
      return this.requests.filter(r => this.isReRouted(r)).length;
    },
    activeColumnFilters() {
      const chips = [];
      for (const col of Object.keys(this.columnFilters)) {
        for (const v of this.columnFilters[col]) {
          chips.push({
            col,
            value: v,
            label: COLUMN_LABELS[col] || col,
            display: this.formatMenuLabel(col, v),
          });
        }
      }
      return chips;
    },
  },
  watch: {
    filteredRequests() {
      if (this.page > this.totalPages) this.page = this.totalPages;
    },
  },
  mounted() {
    this.fetchWaitlist();
    window.addEventListener('scroll', this.onWindowChange, true);
    window.addEventListener('resize', this.onWindowChange);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onWindowChange, true);
    window.removeEventListener('resize', this.onWindowChange);
  },
  methods: {
    async fetchWaitlist() {
      this.loading = true;
      try {
        const headers = await buildAuthHeaders();
        const { data } = await axios.get(config.api_url + 'signup/waitlist', { headers });
        if (data.code === 'SUCCESS') {
          this.requests = (data.data || []).map(r => ({
            ...r,
            matched_company_name: r.matched_company_name || '',
            utm_source: r.utm_source || '',
            status: r.status || '',
            role: r.role || '',
          }));
        }
      } catch (err) {
        console.error('Failed to fetch waitlist:', err);
      }
      this.loading = false;
    },

    async reviewRequest(item, action) {
      this.reviewingId = item.id;
      try {
        const headers = await buildAuthHeaders();
        const { data } = await axios.post(config.api_url + 'signup/review', {
          request_id: item.id,
          action,
        }, { headers });
        if (data.code === 'SUCCESS') {
          await this.fetchWaitlist();
        }
      } catch (err) {
        console.error('Review failed:', err);
      }
      this.reviewingId = null;
    },

    columnLabel(col) { return COLUMN_LABELS[col] || col; },

    // A "re-routed" row is a homeowner waitlist signup whose original
    // referral team didn't claim them, so the backend re-attached them to a
    // different team. Identified by having BOTH the original referral
    // attribution and a (different) matched team.
    isReRouted(row) {
      return !!(row && row.original_referral_tenant_id && row.matched_tenant_id);
    },

    columnValue(row, col) {
      return col === 'utm_source' ? (row.utm_source || '') : (row[col] || '');
    },

    uniqueValues(col) {
      const set = new Set();
      for (const r of this.requests) set.add(this.columnValue(r, col));
      return Array.from(set).sort((a, b) => this.formatMenuLabel(col, a).localeCompare(this.formatMenuLabel(col, b)));
    },

    rolePillStyle(role) {
      if (role === 'agent') return { background: '#FFF3E0', color: '#E65100' };
      if (role === 'vendor') return { background: '#E3F2FD', color: '#1565C0' };
      return { background: '#F3E5F5', color: '#7B1FA2' };
    },

    formatMenuLabel(col, value) {
      if (col === 'utm_source') return this.displaySource(value);
      if (col === 'matched_company_name') return value || 'Need Agent';
      if (col === 'status') return this.displayStatus(value);
      if (col === 'role') return value ? (value.charAt(0).toUpperCase() + value.slice(1)) : '—';
      return value || '—';
    },

    toggleHeaderMenu(col, event) {
      if (this.openMenu === col) {
        this.openMenu = null;
        return;
      }
      if (event && event.currentTarget) {
        const rect = event.currentTarget.getBoundingClientRect();
        // Position the menu flush-left to the button, just below it.
        this.menuPosition = {
          top: Math.round(rect.bottom + 6),
          left: Math.round(rect.left),
          width: Math.max(200, Math.round(rect.width + 40)),
        };
      }
      this.openMenu = col;
    },

    closeHeaderMenu() {
      this.openMenu = null;
    },

    onWindowChange() {
      // Fixed-position menu can't follow scrolling; just close it.
      if (this.openMenu) this.openMenu = null;
    },

    toggleColumnFilter(col, value) {
      const list = this.columnFilters[col];
      const idx = list.indexOf(value);
      if (idx === -1) list.push(value);
      else list.splice(idx, 1);
    },

    removeColumnFilter(col, value) {
      const list = this.columnFilters[col];
      const idx = list.indexOf(value);
      if (idx !== -1) list.splice(idx, 1);
    },

    clearAllColumnFilters() {
      for (const col of Object.keys(this.columnFilters)) this.columnFilters[col] = [];
    },

    resetFilters() {
      this.activeFilter = 'all';
      this.clearAllColumnFilters();
      this.search = '';
    },

    displaySource(s) {
      if (!s) return 'Direct';
      if (s.toLowerCase() === 'real estate agent') return 'Agent';
      return s.charAt(0).toUpperCase() + s.slice(1);
    },

    displayStatus(s) {
      if (!s) return '—';
      // Convert snake_case to Title Case: "signed_up" → "Signed Up"
      return s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    },

    formatDate(date) {
      if (!date) return '—';
      const d = new Date(date);
      const month = d.toLocaleDateString('en-US', { month: 'short' });
      const day = d.getDate();
      const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      return `${month} ${day}, ${time}`;
    },

    statusColor(status) {
      const colors = {
        pending: '#FFF8E1',
        signed_up: '#E8F5E9',
        approved: '#E8F5E9',
        declined: '#FFEBEE',
      };
      return colors[status] || '#F5F5F5';
    },

    statusTextColor(status) {
      const colors = {
        pending: '#F57F17',
        signed_up: '#2E7D32',
        approved: '#2E7D32',
        declined: '#C62828',
      };
      return colors[status] || '#666';
    },
  },
};
</script>

<style scoped>
/* ─── Shell ────────────────────────────────────────────────────── */
.signup-waitlist-page {
  background: #f4f6fb;
}

.waitlist-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 8px 24px rgba(16, 24, 40, 0.04);
  overflow: hidden;
  max-width: 1280px;
  margin: 0 auto;
}

.waitlist-card__header {
  display: flex;
  align-items: center;
  padding: 20px 24px;
  gap: 16px;
  border-bottom: 1px solid #eef2f7;
}

.waitlist-card__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: -0.01em;
}

.waitlist-card__title-chip {
  background: #E3F2FD;
  color: #1565C0;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 999px;
  letter-spacing: 0;
}

.waitlist-card__search {
  position: relative;
  min-width: 320px;
  max-width: 420px;
}

.waitlist-card__search-icon {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  pointer-events: none;
}

.waitlist-card__search-input {
  width: 100%;
  height: 40px;
  padding: 0 14px 0 38px;
  border: 1px solid #d9e1f0;
  border-radius: 10px;
  font-size: 13.5px;
  color: #0f172a;
  background: #f8fafc;
  outline: none;
  transition: border-color 120ms ease, background 120ms ease, box-shadow 120ms ease;
}

.waitlist-card__search-input::placeholder { color: #94a3b8; }

.waitlist-card__search-input:focus {
  border-color: #2F6FED;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(47, 111, 237, 0.12);
}

/* ─── Stat bar ─────────────────────────────────────────────────── */
.waitlist-stats-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  border-bottom: 1px solid #eef2f7;
  background: #fafbfd;
  flex-wrap: wrap;
}

.waitlist-stats-bar__item {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: transparent;
  border: 1px solid transparent;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease;
  font-family: inherit;
}

.waitlist-stats-bar__item:hover {
  background: #ffffff;
  border-color: #e2e8f0;
}

.waitlist-stats-bar__item--active {
  background: #ffffff;
  border-color: #2F6FED;
  box-shadow: 0 1px 2px rgba(47, 111, 237, 0.12);
}

.waitlist-stats-bar__label {
  font-size: 12.5px;
  font-weight: 500;
  color: #475467;
}

.waitlist-stats-bar__item--active .waitlist-stats-bar__label {
  color: #0f172a;
  font-weight: 600;
}

.waitlist-stats-bar__count {
  font-size: 12.5px;
  font-weight: 700;
  color: #0f172a;
  background: #EEF4FF;
  padding: 2px 8px;
  border-radius: 999px;
  min-width: 22px;
  text-align: center;
}

.waitlist-stats-bar__item--active .waitlist-stats-bar__count {
  background: #DBEAFE;
  color: #1D4ED8;
}

.waitlist-stats-bar__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.waitlist-stats-bar__dot--orange { background: #F97316; }
.waitlist-stats-bar__dot--green { background: #22C55E; }
.waitlist-stats-bar__dot--yellow { background: #EAB308; }
.waitlist-stats-bar__dot--blue { background: #2563EB; }

.waitlist-stats-bar__divider {
  width: 1px;
  height: 20px;
  background: #e2e8f0;
}

/* ─── Active column filter chips ───────────────────────────────── */
.waitlist-active-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  padding: 10px 24px;
  border-bottom: 1px solid #eef2f7;
  background: #fafbfd;
}

.waitlist-filter-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #EEF4FF;
  color: #2F6FED;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
}

.waitlist-filter-pill__label {
  color: #64748b;
  font-weight: 500;
  text-transform: none;
}

.waitlist-filter-pill__close {
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  margin-left: 2px;
  padding: 0 2px;
}

.waitlist-filter-pill__close:hover { color: #1D4ED8; }

.waitlist-filter-clear {
  font-size: 11.5px;
  font-weight: 600;
  color: #2F6FED;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
}

/* ─── Empty state ──────────────────────────────────────────────── */
.waitlist-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 56px 24px;
  color: #64748b;
  text-align: center;
}

.waitlist-empty p {
  margin: 4px 0;
  font-size: 14px;
  font-weight: 500;
  color: #334155;
}

.waitlist-empty__sub {
  font-size: 13px !important;
  color: #94a3b8 !important;
  font-weight: 400 !important;
}

/* ─── Table ────────────────────────────────────────────────────── */
.waitlist-table-wrap {
  overflow-x: auto;
  scrollbar-gutter: stable;
}

.waitlist-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: auto;
}

.waitlist-table thead th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  vertical-align: middle;
}

.waitlist-th--name { padding-left: 24px !important; }
.waitlist-th--date { min-width: 130px; }
.waitlist-th--actions {
  padding-right: 24px !important;
  min-width: 180px;
  text-align: right;
}

.waitlist-th-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  cursor: pointer;
  font-weight: 700;
  transition: color 120ms ease;
}

.waitlist-th-button:hover,
.waitlist-th-button--active { color: #2F6FED; }

.waitlist-th-button__chevron {
  opacity: 0.7;
  transition: transform 120ms ease;
}

.waitlist-th-button--active .waitlist-th-button__chevron {
  transform: rotate(180deg);
  opacity: 1;
}

/* ─── Floating dropdown menu (position: fixed) ─────────────────── */
.waitlist-th-menu {
  position: fixed;
  z-index: 1000;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.14);
  padding: 6px;
  max-height: 320px;
  overflow-y: auto;
  font-family: inherit;
}

.waitlist-th-menu__header {
  padding: 6px 10px 8px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 4px;
}

.waitlist-th-menu__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 400;
  color: #0f172a;
  text-transform: none;
  letter-spacing: 0;
  font-family: inherit;
}

.waitlist-th-menu__item:hover { background: #f1f5f9; }

.waitlist-th-menu__item input[type="checkbox"] {
  width: 14px;
  height: 14px;
  accent-color: #2F6FED;
  cursor: pointer;
  flex-shrink: 0;
}

.waitlist-th-menu__clear {
  display: block;
  width: 100%;
  margin-top: 4px;
  padding: 8px;
  background: none;
  border: none;
  border-top: 1px solid #e2e8f0;
  font-size: 12px;
  font-weight: 600;
  color: #2F6FED;
  cursor: pointer;
  text-transform: none;
  letter-spacing: 0;
  font-family: inherit;
}

/* ─── Body cells ───────────────────────────────────────────────── */
.waitlist-row td {
  font-size: 13px;
  color: #0f172a;
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  white-space: nowrap;
}

.waitlist-row:hover td { background: #f8fafc; }

.waitlist-cell--name { padding-left: 24px !important; }

.waitlist-name {
  font-weight: 600;
  color: #0f172a;
}

.waitlist-cell--muted { color: #475467; }
.waitlist-cell--dash { color: #cbd5e1; }
.waitlist-cell--matched { color: #15803D; font-weight: 600; }
.waitlist-cell--date { color: #475467; font-size: 12.5px; }

.waitlist-cell--actions {
  padding-right: 24px !important;
  text-align: right;
}

.waitlist-pill {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 999px;
  text-transform: capitalize;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.waitlist-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;
  margin-left: 6px;
  font-family: inherit;
}

.waitlist-action-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.waitlist-action-btn--approve {
  background: #19A99D;
  color: #ffffff;
}

.waitlist-action-btn--approve:hover:not(:disabled) { background: #138f85; }

.waitlist-action-btn--decline {
  background: transparent;
  color: #c72525;
  border-color: #fecaca;
}

.waitlist-action-btn--decline:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #c72525;
}

/* ─── Pagination ───────────────────────────────────────────────── */
.waitlist-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
  padding: 14px 24px;
  border-top: 1px solid #eef2f7;
  font-size: 12.5px;
  color: #475467;
}

.waitlist-pagination__size {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.waitlist-pagination__select {
  height: 28px;
  padding: 0 22px 0 8px;
  border: 1px solid #d9e1f0;
  border-radius: 6px;
  background: #ffffff;
  font-size: 12.5px;
  color: #0f172a;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='%23667085' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 6px center;
  cursor: pointer;
}

.waitlist-pagination__nav {
  display: inline-flex;
  gap: 4px;
}

.waitlist-pagination__btn {
  width: 28px;
  height: 28px;
  border: 1px solid #d9e1f0;
  background: #ffffff;
  color: #475467;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 120ms ease, color 120ms ease, background 120ms ease;
}

.waitlist-pagination__btn:hover:not(:disabled) {
  border-color: #2F6FED;
  color: #2F6FED;
}

.waitlist-pagination__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ─── Narrow viewport ──────────────────────────────────────────── */
@media (max-width: 960px) {
  .waitlist-card__header {
    flex-wrap: wrap;
  }
  .waitlist-card__search {
    min-width: 100%;
    max-width: 100%;
    flex: 1 1 100%;
  }
}
</style>
